import { useState } from 'react'
import { useActionDispatch } from '../../hooks/useActionDispatch'
import { BodyMap, buildMissingZones } from './BodyMap'

interface Injury {
  id: string
  body_location: string
  laterality: string | null
  grade: string
  injury_levels: number
  wound_type: string | null
  aspect: string
  is_treated: boolean
  healing_rate: number | null
  days_to_roll: number
  is_infected: boolean
  is_bleeder: boolean
  healed_at: string | null
}

interface Props {
  character: {
    id: string
    injuries: Injury[]
    fatigue_levels: number
    active_conditions: string[] | null
    medical_traits: Array<{ label?: string; id?: string }> | null
  }
  campaignId?: string
  canEdit?: boolean
  armourProtection?: Record<string, { blunt: number; edge: number; point: number; fire: number }>
}

const GRADE_COLORS: Record<string, string> = {
  M1: 'bg-success',
  S2: 'bg-warning',
  S3: 'bg-orange-600',
  G4: 'bg-danger',
  G5: 'bg-red-800',
  K4: 'bg-ink',
  K5: 'bg-ink',
}

const WOUND_LOCATIONS = [
  'Skull','Face','Neck','Shoulder-L','Shoulder-R','Upper-Arm-L','Upper-Arm-R',
  'Elbow-L','Elbow-R','Forearm-L','Forearm-R','Hand-L','Hand-R',
  'Thorax','Abdomen','Groin','Hip-L','Hip-R','Thigh-L','Thigh-R',
  'Knee-L','Knee-R','Calf-L','Calf-R','Foot-L','Foot-R',
]

export function InjuryTracker({ character, campaignId, canEdit, armourProtection }: Props) {
  const { dispatch } = useActionDispatch(character.id, campaignId ?? '')
  const [busy, setBusy] = useState<string | null>(null)
  const [treatingId, setTreatingId] = useState<string | null>(null)
  const [treatHR, setTreatHR] = useState(3)
  const [showWound, setShowWound] = useState(false)
  const [woundLocation, setWoundLocation] = useState('Thorax')
  const [woundLaterality, setWoundLaterality] = useState('N/A')
  const [woundGrade, setWoundGrade] = useState('M1')
  const [woundAspect, setWoundAspect] = useState('Edge')
  const [woundImpact, setWoundImpact] = useState(0)
  const [actionBusy, setActionBusy] = useState(false)
  const [error, setError] = useState('')
  const active = character.injuries.filter(i => i.healed_at === null)

  const run = async (injId: string, fn: () => Promise<unknown>) => {
    setBusy(injId)
    try { await fn() } catch { /* refresh shows current state */ }
    setBusy(null)
  }

  const runAction = async (fn: () => Promise<unknown>) => {
    setActionBusy(true); setError('')
    try { await fn() } catch (e) { setError(e instanceof Error ? e.message : 'Failed') }
    setActionBusy(false)
  }

  return (
    <div className="border border-parchment-dark rounded p-4 mb-4 bg-white">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-cinzel font-semibold text-sm text-accent uppercase tracking-wider mb-3 pb-2 border-b-2 border-accent/20">
          Injuries
          {active.length > 0 && <span className="text-danger ml-2">({active.length})</span>}
        </h3>
        {canEdit && (
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 border border-parchment-dark rounded px-2 py-0.5">
              <span className="text-xs font-medium">Fatigue: {character.fatigue_levels}</span>
              <button onClick={() => runAction(() => dispatch('ADD_FATIGUE', { amount: 1 }))} disabled={actionBusy}
                className="px-1.5 py-0 text-xs hover:bg-parchment-dark rounded">+</button>
              <button onClick={() => runAction(() => dispatch('REMOVE_FATIGUE', { amount: 1 }))} disabled={actionBusy || character.fatigue_levels <= 0}
                className="px-1.5 py-0 text-xs hover:bg-parchment-dark rounded">-</button>
            </div>
            <button onClick={() => setShowWound(!showWound)}
              className="text-xs px-2 py-0.5 bg-danger text-white rounded hover:opacity-90">
              + Wound
            </button>
          </div>
        )}
      </div>

      {error && <p className="text-xs text-danger mb-2">{error}</p>}

      {/* Record Wound form */}
      {showWound && canEdit && (
        <div className="border border-parchment-dark rounded p-3 mb-3 bg-parchment">
          <div className="grid grid-cols-2 gap-2 text-sm mb-2">
            <div>
              <label className="block text-xs font-medium mb-0.5">Location</label>
              <select value={woundLocation} onChange={e => setWoundLocation(e.target.value)}
                className="w-full px-2 py-1 border border-parchment-dark rounded text-sm">
                {WOUND_LOCATIONS.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-0.5">Laterality</label>
              <select value={woundLaterality} onChange={e => setWoundLaterality(e.target.value)}
                className="w-full px-2 py-1 border border-parchment-dark rounded text-sm">
                <option value="N/A">N/A</option><option value="Left">Left</option><option value="Right">Right</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-0.5">Grade</label>
              <select value={woundGrade} onChange={e => setWoundGrade(e.target.value)}
                className="w-full px-2 py-1 border border-parchment-dark rounded text-sm">
                {['M1','S2','S3','G4','G5','K4','K5'].map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-0.5">Aspect</label>
              <select value={woundAspect} onChange={e => setWoundAspect(e.target.value)}
                className="w-full px-2 py-1 border border-parchment-dark rounded text-sm">
                {['Blunt','Edge','Point','Fire','Frost'].map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-0.5">Effective Impact</label>
              <input type="number" value={woundImpact} onChange={e => setWoundImpact(parseInt(e.target.value) || 0)}
                className="w-full px-2 py-1 border border-parchment-dark rounded text-sm" />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => runAction(async () => {
              await dispatch('RECEIVE_WOUND', { body_location: woundLocation, laterality: woundLaterality, grade: woundGrade, aspect: woundAspect, effective_impact: woundImpact })
              setShowWound(false)
            })} disabled={actionBusy}
              className="px-3 py-1 text-sm bg-danger text-white rounded hover:opacity-90 disabled:opacity-50">
              {actionBusy ? 'Saving...' : 'Record'}
            </button>
            <button onClick={() => setShowWound(false)}
              className="px-3 py-1 text-sm border border-parchment-dark rounded hover:bg-parchment-dark">Cancel</button>
          </div>
        </div>
      )}

      <BodyMap injuries={character.injuries} armourProtection={armourProtection} missingLimbs={buildMissingZones([
        ...(character.active_conditions ?? []),
        ...(character.medical_traits ?? []).map(t => t.id ?? ''),
      ])} />

      {active.length === 0 ? (
        <p className="text-ink-light text-sm mt-2">No active injuries.</p>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-parchment-dark text-left">
              <th className="py-1">Location</th>
              <th>Grade</th>
              <th>Type</th>
              <th>HR</th>
              <th>Days</th>
              <th>Status</th>
              {canEdit && <th></th>}
            </tr>
          </thead>
          <tbody>
            {active.map(inj => (
              <tr key={inj.id} className="border-b border-parchment/50">
                <td className="py-1">
                  {inj.body_location}
                  {inj.laterality && inj.laterality !== 'N/A' && ` (${inj.laterality[0]})`}
                </td>
                <td>
                  <span className={`${GRADE_COLORS[inj.grade] ?? 'bg-ink-light'} text-white px-1.5 py-0.5 rounded text-xs font-bold`}>
                    {inj.grade}
                  </span>
                </td>
                <td>{inj.wound_type ?? inj.aspect}</td>
                <td>{inj.healing_rate ?? '—'}</td>
                <td>{inj.days_to_roll}</td>
                <td>
                  {inj.is_treated && <span className="text-success mr-1">T</span>}
                  {inj.is_infected && <span className="text-purple-600 mr-1">INF</span>}
                  {inj.is_bleeder && <span className="text-danger">BLD</span>}
                </td>
                {canEdit && (
                  <td className="text-right space-x-1">
                    {!inj.is_treated && (
                      <button
                        onClick={() => setTreatingId(treatingId === inj.id ? null : inj.id)}
                        className="px-1.5 py-0.5 text-xs text-blue-600 border border-blue-300 rounded hover:bg-blue-50"
                      >Treat</button>
                    )}
                    {inj.is_treated && !inj.is_infected && (
                      <button
                        onClick={() => run(inj.id, () => dispatch('INFECT_WOUND', { injury_id: inj.id, infection_hr: 1 }))}
                        disabled={busy === inj.id}
                        className="px-1.5 py-0.5 text-xs text-purple-600 border border-purple-300 rounded hover:bg-purple-50 disabled:opacity-50"
                      >Infect</button>
                    )}
                    <button
                      onClick={() => run(inj.id, () => dispatch('HEAL_WOUND', { injury_id: inj.id, healed: true }))}
                      disabled={busy === inj.id}
                      className="px-1.5 py-0.5 text-xs text-success border border-success/30 rounded hover:bg-green-50 disabled:opacity-50"
                    >{busy === inj.id ? '...' : 'Heal'}</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>

          {/* Inline treat form */}
          {treatingId && (
            <tfoot>
              <tr>
                <td colSpan={canEdit ? 7 : 6} className="pt-2">
                  <div className="flex items-center gap-2 bg-blue-50 p-2 rounded text-xs">
                    <span className="font-medium">Treat wound — Healing Rate:</span>
                    <select value={treatHR} onChange={e => setTreatHR(parseInt(e.target.value))}
                      className="px-1 py-0.5 border border-blue-300 rounded">
                      {[1,2,3,4,5,6].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                    <button
                      onClick={() => run(treatingId, async () => {
                        await dispatch('TREAT_WOUND', { injury_id: treatingId, healing_rate: treatHR })
                        setTreatingId(null)
                      })}
                      disabled={busy === treatingId}
                      className="px-2 py-0.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                    >Apply</button>
                    <button onClick={() => setTreatingId(null)}
                      className="px-2 py-0.5 border border-blue-300 rounded hover:bg-blue-100">Cancel</button>
                  </div>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      )}
    </div>
  )
}
